// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: d7b256bb-ebb4-42ad-b072-aa3ed9f9a0de

// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary

// ---------------------------------------------------------------------------
// Primordia — What's coming
// ---------------------------------------------------------------------------
// A visible roadmap so colleagues can see what is being built without asking,
// and a clear route to ask for something that is not here.
//
// THIS CONTENT IS WRITTEN BY HAND, DELIBERATELY. It is NOT generated from the
// engineering backlog. The backlog names clients, quotes estate sizes, records
// which client's data is loaded where, and carries internal notes about
// commercial and security work. None of that belongs on a page that anyone with
// a login can open, and a page that auto-syncs from an internal task list will
// eventually leak one of those the day somebody files a ticket with a client
// name in the title. So the two are kept separate on purpose: when the backlog
// changes, someone updates this file with a sentence a client could read.
//
// Rules for editing this file:
//   1. No client names. Ever. Not even ones you think are public.
//   2. No estate sizes, spend figures, or anything traceable to one engagement.
//   3. Describe the capability, not the implementation or the ticket.
//   4. Say honestly where something is. "Exploring" is a real status; pretending
//      everything is nearly done is how a roadmap stops being believed.
// ---------------------------------------------------------------------------

import React, { useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Chip, Stack, Divider, TextField, Button,
  ToggleButton, ToggleButtonGroup, Alert, AlertTitle, Link,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import { getSession } from './auth';
import HelpBubble from './HelpBubble';

const NAVY = '#021F4B';
const BLUE = '#0D5B9A';
const GREY = '#5C6B7A';
const HEAD = 'sectionHeader.main';

const REQUESTS_TO = 'FScalzo@optimumhit.com';

// Status vocabulary, deliberately small. Anything vaguer than these four is a
// way of avoiding a commitment.
const STATUS = {
  live: { label: 'Available now', color: '#2E7D32', order: 0 },
  building: { label: 'Being built', color: BLUE, order: 1 },
  next: { label: 'Planned', color: '#EF6C00', order: 2 },
  exploring: { label: 'Exploring', color: GREY, order: 3 },
};

// ---------------------------------------------------------------------------
// The roadmap itself. Capability-level, client-safe.
// ---------------------------------------------------------------------------
const ITEMS = [
  // ---- Available now ------------------------------------------------------
  { status: 'live', area: 'Discovery', title: 'Application inventory and fact sheets',
    detail: 'A full application catalogue with the fields a migration actually needs — ownership, criticality, hosting, disposition, DR posture, lessons learned and cloud gotchas.' },
  { status: 'live', area: 'Discovery', title: 'Spreadsheet import with column mapping',
    detail: 'Upload an inventory in whatever shape the client sends it. Columns are auto-mapped to a common field set and you confirm anything ambiguous. Re-importing an updated file updates records instead of duplicating them.' },
  { status: 'live', area: 'Discovery', title: 'Infrastructure import and server mapping',
    detail: 'Load virtual estate exports, then map servers to the applications and environments they belong to using rules, a worksheet round-trip, or by hand.' },
  { status: 'live', area: 'Discovery', title: 'Environment and version tracking',
    detail: 'Production, test, training, DR and one-off instances are tracked separately, each with its own version, cutover date and wave — because they migrate on different dates with different risk.' },
  { status: 'live', area: 'Analysis', title: 'Landscape, matrix and circle heat maps',
    detail: 'Portfolio views colour-coded by any captured field, so a gap in the data is as visible as the data itself.' },
  { status: 'live', area: 'Analysis', title: 'Portfolio and radar charts',
    detail: 'Plot any two dimensions against each other — functional against technical fit, disposition against criticality, RTO against RPO — with quadrant reading for rationalisation decisions.' },
  { status: 'live', area: 'Analysis', title: 'Obsolescence and technology risk',
    detail: 'Which operating systems are past end of support, which end soon, and — just as importantly — how much of the estate cannot be assessed yet because the source data is too vague.' },
  { status: 'live', area: 'Analysis', title: 'Right-sizing from utilisation',
    detail: 'Where an inventory carries real CPU, memory and IOPS telemetry, size the target from observed peak rather than from what was provisioned years ago.' },
  { status: 'live', area: 'Analysis', title: 'Cost modelling on live cloud prices',
    detail: 'Estimates priced against current AWS and Azure list prices rather than a stale rate card, with private pricing discounts, support tiers and additional cost lines modelled on top.' },
  { status: 'live', area: 'Architecture', title: 'Relationship and data flow diagrams',
    detail: 'Printable relationship maps and an integration view showing what routes through an interface engine and what connects point-to-point — the latter being the harder migration problem.' },
  { status: 'live', area: 'Architecture', title: 'Target architecture from Epic sizing guides',
    detail: 'Import an Epic Cloud Specifications Guide and render the future state by stamp and tier, with version-to-version comparison when a guide is reissued.' },
  { status: 'live', area: 'Planning', title: 'Migration waves and cutover readiness',
    detail: 'Dependency-ordered wave planning and a readiness view that answers whether an application can actually cut over yet.' },
  { status: 'live', area: 'Delivery', title: 'Reports gallery and saved views',
    detail: 'Save a report configuration, name it, favourite it and share it within your workspace. Grouped by the kind of engagement work it supports rather than by chart type.' },
  { status: 'live', area: 'Delivery', title: 'Discovery forms round-trip',
    detail: 'Export a questionnaire as a form, have it filled in offline, and load the answers back — with the survey screen tracking what is still outstanding on this browser.' },
  { status: 'live', area: 'Platform', title: 'Per-client workspaces with strict separation',
    detail: 'Each engagement has its own workspace. Separation is enforced by the database itself, not only by the application, so one client can never see another.' },
  { status: 'live', area: 'Platform', title: 'Change history',
    detail: 'Every field change is recorded with who changed it, when, and what it was before.' },

  // ---- Being built --------------------------------------------------------
  { status: 'live', area: 'Platform', title: 'Single sign-on',
    detail: 'Sign in with a federated account (Google today; a client directory is a configuration entry away). Approval is per account, local sign-in stays as the fallback, and lost invitations recover themselves from the sign-in page.' },
  { status: 'live', area: 'Discovery', title: 'Naming convention decoder',
    detail: 'Every organisation encodes meaning in its host names. Build up that vocabulary per client and use it to explain an estate that would otherwise be thousands of anonymous machines.' },
  { status: 'live', area: 'Delivery', title: 'Master application catalog',
    detail: 'Optimum-curated product knowledge — typical hosting, default disposition, de-identified lessons and cloud gotchas — built by reconciling what real client estates actually run, so every engagement starts warmer than the last.' },
  { status: 'live', area: 'Delivery', title: 'In-app help and wiki',
    detail: 'A “?” beside screens and fields explains what things mean, backed by a searchable help center. Every entry is written from what the product actually does.' },
  { status: 'live', area: 'Platform', title: 'Recycle bin with a 30-day window',
    detail: 'Deleting a record moves it to a trash that Optimum can restore from for 30 days; after that it is purged automatically.' },

  // ---- Being built --------------------------------------------------------
  { status: 'live', area: 'Platform', title: 'Contracts and renewals',
    detail: 'Vendor contracts, support expirations and a renewal calendar per client — so “what renews during the migration window” has an answer on day one.' },
  { status: 'live', area: 'Analysis', title: 'Hardware and OS lifecycle dates',
    detail: 'End-of-life and end-of-support dates on the inventory itself, feeding the obsolescence view with real vendor dates ahead of inference.' },
  { status: 'live', area: 'Discovery', title: 'Reading scanned documents',
    detail: 'Scanned PDFs and images are read with OCR — text and tables, with per-cell confidence — and flow into the same evidence-quoted extraction review as pasted text.' },

  // ---- Planned ------------------------------------------------------------
  { status: 'live', area: 'Analysis', title: 'Actual cloud spend from the payer account',
    detail: 'A workspace connects its own AWS payer account — read-only, created by its own admin in its own console, revocable by deleting the stack — and the FinOps screen shows real consolidated spend by month, account and service.' },
  { status: 'live', area: 'Analysis', title: 'FinOps tooling',
    detail: 'Cost anomalies, savings recommendations and showback by application, live against the connected AWS payer account. An Azure estate connects through Azure Lighthouse — delegated read-only access with no client secret held, deployed from the client’s own portal per subscription — and its per-subscription spend is read through that delegation on the same FinOps screen. Anomalies, recommendations and showback remain AWS-only today.' },
  { status: 'live', area: 'Architecture', title: 'Built-versus-specified audit',
    detail: 'Compare what was actually deployed in a cloud account against what the sizing guide specified, and surface the drift.' },
  { status: 'live', area: 'Delivery', title: 'Reusable engagement templates',
    detail: 'Start a new engagement from a proven template — fact sheet structures, questionnaires and reference architectures — instead of rebuilding from scratch each time.' },
  { status: 'live', area: 'Analysis', title: 'Business capability map',
    detail: 'A capability hierarchy that applications map onto — heat-coloured by TIME, 6R, fit or completion — so rationalisation can be argued from what the business does rather than from a flat list of software.' },
  { status: 'live', area: 'Delivery', title: 'Survey campaigns with email notification',
    detail: 'Assign a discovery campaign to workspace users, notify them by email (with explicit confirmation, and only people with an enabled account), and track who has finished centrally.' },
  { status: 'live', area: 'Platform', title: 'Software licenses and entitlements',
    detail: 'Licensed quantity, unit and proof documents on every contract, plus license positions — licensed versus derived-or-labeled consumption, side by side. True-up trend alerting is the next layer.' },
  { status: 'live', area: 'Platform', title: 'Client sign-on with their own directory',
    detail: 'A client organisation signs in with their own identity provider; approval stays per account on Primordia\u2019s side, and attaching a directory is configuration, not engineering.' },

  // ---- Exploring ----------------------------------------------------------
  { status: 'live', area: 'Discovery', title: 'Capture from meeting transcripts',
    detail: 'Paste a meeting transcript and the facts it contains are proposed with the quote that supports each one — a consultant confirms before anything is recorded. Live audio pull from meeting platforms is still being explored.' },
  { status: 'exploring', area: 'Platform', title: 'Two-way connectors to other EA tools',
    detail: 'Exchange data with the tools clients already run, mapping fields between their model and ours.' },
  { status: 'exploring', area: 'Delivery', title: 'Mobile access',
    detail: 'Read-only access to the catalogue and reports from a phone or tablet while on site.' },
  { status: 'exploring', area: 'Platform', title: 'Per-client access for Optimum staff',
    detail: 'Grant a consultant the specific clients they are engaged on, rather than all or none. '
      + 'Today access is either a single workspace or every client at once, so staffing one engagement '
      + 'means sight of every other client too.' },
];

const AREAS = ['All', 'Discovery', 'Analysis', 'Architecture', 'Planning', 'Delivery', 'Platform'];

function StatusChip({ status }) {
  const s = STATUS[status] || STATUS.exploring;
  return (
    <Chip
      size="small"
      label={s.label}
      sx={{
        bgcolor: s.color, color: '#fff', fontWeight: 700, fontSize: 11,
        height: 22, letterSpacing: 0.3,
      }}
    />
  );
}

export default function Roadmap({ onRequest }) {
  const [area, setArea] = useState('All');
  const [idea, setIdea] = useState('');
  const session = getSession();

  const grouped = useMemo(() => {
    const rows = ITEMS.filter((i) => area === 'All' || i.area === area);
    const order = Object.keys(STATUS).sort((a, b) => STATUS[a].order - STATUS[b].order);
    return order
      .map((k) => ({ key: k, items: rows.filter((r) => r.status === k) }))
      .filter((g) => g.items.length);
  }, [area]);

  const counts = useMemo(() => {
    const c = {};
    Object.keys(STATUS).forEach((k) => { c[k] = ITEMS.filter((i) => i.status === k).length; });
    return c;
  }, []);

  // A mailto rather than a ticket queue, on purpose: a request that lands in a
  // real inbox gets a reply from a person. Prefilled so the sender does not have
  // to explain who they are or where they were.
  const mailto = useMemo(() => {
    const subject = 'Primordia — feature request';
    const body = [
      idea.trim() || '(describe what you would like Primordia to do)',
      '',
      '---',
      `Sent from Primordia by ${session?.email || 'a signed-out user'}`,
      session?.tenantName ? `Workspace: ${session.tenantName}` : '',
    ].filter(Boolean).join('\n');
    return `mailto:${REQUESTS_TO}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }, [idea, session]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
      <Typography variant="h5" sx={{ color: HEAD, fontWeight: 800 }}>
        What&rsquo;s coming in Primordia <HelpBubble topic="whatsNew" />
      </Typography>
      <Typography variant="body2" sx={{ color: GREY, mt: 0.5, mb: 2.5 }}>
        What is available today, what is being built, and what is still an idea. If something you
        need is missing, ask — the list below is shaped by what people actually run into on
        engagements, not by what is easiest to build.
      </Typography>

      <Stack direction="row" spacing={1} sx={{ mb: 2.5, flexWrap: 'wrap', gap: 1 }}>
        {Object.keys(STATUS)
          .sort((a, b) => STATUS[a].order - STATUS[b].order)
          .map((k) => (
            <Chip
              key={k}
              size="small"
              label={`${STATUS[k].label}: ${counts[k]}`}
              variant="outlined"
              // A count is a question ("which 33?"), so make it answer itself.
              // Disabled at zero rather than hidden: "Planned: 0" is meaningful
              // information, and a chip that scrolls nowhere is worse than one
              // that plainly cannot be clicked.
              clickable={counts[k] > 0}
              onClick={counts[k] > 0 ? () => {
                const el = document.getElementById(`roadmap-${k}`);
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
              } : undefined}
              sx={{
                borderColor: STATUS[k].color,
                color: STATUS[k].color,
                fontWeight: 700,
                opacity: counts[k] > 0 ? 1 : 0.45,
                cursor: counts[k] > 0 ? 'pointer' : 'default',
              }}
            />
          ))}
      </Stack>

      <ToggleButtonGroup
        size="small"
        exclusive
        value={area}
        onChange={(e, v) => v && setArea(v)}
        sx={{ mb: 3, flexWrap: 'wrap' }}
      >
        {AREAS.map((a) => (
          <ToggleButton key={a} value={a} sx={{ textTransform: 'none', px: 1.5 }}>{a}</ToggleButton>
        ))}
      </ToggleButtonGroup>

      {grouped.map((g) => (
        <Box key={g.key} id={`roadmap-${g.key}`} sx={{ mb: 3.5, scrollMarginTop: 88 }}>
          <Stack direction="row" alignItems="center" spacing={1.2} sx={{ mb: 1.2 }}>
            <StatusChip status={g.key} />
            <Divider sx={{ flex: 1 }} />
          </Stack>
          <Stack spacing={1.2}>
            {g.items.map((item) => (
              <Paper key={item.title} variant="outlined" sx={{ p: 1.8, borderLeft: `4px solid ${STATUS[g.key].color}` }}>
                <Stack direction="row" spacing={1} alignItems="baseline" sx={{ flexWrap: 'wrap' }}>
                  <Typography sx={{ fontWeight: 700, color: HEAD }}>{item.title}</Typography>
                  <Chip size="small" label={item.area} variant="outlined" sx={{ height: 19, fontSize: 10, color: GREY }} />
                </Stack>
                <Typography variant="body2" sx={{ color: GREY, mt: 0.6 }}>{item.detail}</Typography>
              </Paper>
            ))}
          </Stack>
        </Box>
      ))}

      <Alert severity="info" sx={{ mb: 2 }}>
        <AlertTitle>Dates are deliberately absent</AlertTitle>
        Items move between these groups as engagement work reveals what is genuinely needed. A date
        on this page would be a guess, and a roadmap people stop believing is worse than no roadmap.
        If timing matters for something you are working on, ask directly and you will get a straight
        answer about where it sits.
      </Alert>

      <Paper variant="outlined" sx={{ p: 2.2, borderColor: BLUE }}>
        <Typography sx={{ color: HEAD, fontWeight: 800, mb: 0.5 }}>Ask for something</Typography>
        <Typography variant="body2" sx={{ color: GREY, mb: 1.5 }}>
          Missing a field, a report, an import format, or a way of working? Describe it below and it
          opens an email to Frank Scalzo. Real examples from a live engagement carry the most weight
          — what you were trying to do, and what stopped you.
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={3}
          size="small"
          placeholder="What would you like Primordia to do?"
          value={idea}
          onChange={(e) => setIdea(e.target.value)}
          sx={{ mb: 1.5 }}
        />
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<EmailIcon />}
            href={mailto}
            sx={{ bgcolor: NAVY, '&:hover': { bgcolor: BLUE } }}
          >
            Send the request
          </Button>
          {onRequest && (
            <Button variant="outlined" onClick={onRequest}>
              Log it in the product instead
            </Button>
          )}
          <Typography variant="caption" sx={{ color: GREY, flexBasis: '100%' }}>
            The email opens your mail client — nothing is sent from this page and nothing is stored
            here, so it gets a reply from a person but leaves no record.
            {onRequest && ' Logging it in the product files it as an idea in the feedback queue, '
              + 'where it is triaged in Manage \u2192 Administration alongside everything else and '
              + 'does not depend on anyone remembering an inbox.'}
            {' '}Or email <Link href={`mailto:${REQUESTS_TO}`}>{REQUESTS_TO}</Link> directly.
          </Typography>
        </Stack>
      </Paper>
    </Box>
  );
}
